/**
 * Material Taxonomy Redux Actions
 * Async thunks for fetching and managing Material Taxonomy data
 * Reference: Special Ingredients actions pattern
 */

import { AppDispatch } from '../../../../redux/store'
import { getFunctionalUnit, getFunctionalUnitConfiguration } from './api'
import {
  setLoading,
  setFunctionalUnit,
  setConfiguration,
  setError,
} from '../../../../redux/materialTaxonomySlice'
import { showCorrelationIdError } from '../../../../redux/correlationIdSlice'

/**
 * Fetch functional unit by ID
 * @param id - Functional unit ID
 * @param replace - Optional navigation replace function for 404 handling
 */
export const fetchMaterialTaxonomy =
  (id: string, replace?: () => void) => async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoading(true))
      const { data: functionalUnitData } = await getFunctionalUnit(id)
      dispatch(setFunctionalUnit(functionalUnitData))
    } catch (error: any) {
      console.error('Failed to fetch material taxonomy:', error)

      // Handle correlation ID errors
      if (error.showCorrelationId) {
        dispatch(showCorrelationIdError(error.CorrelationId))
      }

      // Handle 404 - navigate away
      if (error.response?.status === 400 || error.response?.status === 404) {
        if (replace) {
          replace()
        }
      }

      dispatch(setError(error.message || 'Failed to load functional unit'))
    } finally {
      dispatch(setLoading(false))
    }
  }

/**
 * Fetch configuration for functional unit
 * For new records, use id = '0' to get empty path with enum dropdowns
 * For existing records, returns full path + enum dropdowns
 * @param id - Functional unit ID or '0' for new
 */
export const fetchMaterialTaxonomyConfiguration = (id: string) => async (dispatch: AppDispatch) => {
  try {
    const { data: configurationData } = await getFunctionalUnitConfiguration(id)
    dispatch(setConfiguration(configurationData))
  } catch (error: any) {
    console.error('Failed to fetch material taxonomy configuration:', error)

    // Handle correlation ID errors
    if (error.showCorrelationId) {
      dispatch(showCorrelationIdError(error.CorrelationId))
    }

    dispatch(setError(error.message || 'Failed to load configuration'))
  }
}
